import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import styled from 'styled-components';
import { withFormik, Form } from 'formik';
import { useTranslation } from 'react-i18next';
import differenceInYears from 'date-fns/difference_in_years';
import { dateFormatter, dateUtil } from '@figure/frontlib-util';
import {
  Button,
  CheckboxInput,
  DropdownSelect,
  EmailInput,
  InlineAnchor,
  PhoneInput,
  Typography,
} from '@figure/frontlib-gui';
import { isFigure } from 'shared/utils/ThemeHelper';
import { AddressSectionForm, NameSectionForm, PasswordSectionForm } from 'components/Form';
import { CurrencyInput } from 'shared/components';
import { PageHeader, Error } from 'shared/components/Typography';
import { Financing, Profile, AgeRestriction, LivingArrangements } from 'shared/constants';
import { SMS_EXPERIMENT_MAP } from 'shared/constants/sms-consent';

const ConsentCheckbox = styled(CheckboxInput)`
  + span {
    min-width: 2.4rem;
    height: 2.4rem;
    flex-basis: 2.4rem;
  }
`;

const AgreeButtonDescription = styled(Typography.SmallBodyGrey)`
  margin-bottom: 3.2rem;
`;
const AgreeButtonContainer = styled.div`
  & > * {
    margin-bottom: 1.6rem;
  }
`;

const DisclosureLegal = styled(Typography.SmallBodyGrey)`
  text-align: center;
`;

const MIN_INCOME_WARNING_VALUE = 20000;

const StartApplication = ({
  values,
  errors,
  touched,
  handleSubmit,
  setFieldValue,
  setFieldTouched,
  serverError,
  isAuthenticated,
  profile,
  prePopulatedData,
  isSubmitting,
  smsData,
}) => {
  const [incomeWarningCheckboxChecked, setIncomeWarningCheckboxChecked] = useState(false);
  const [incomeBlurred, setIncomeBlurred] = useState(false);
  const [employmentBlurred, setEmploymentBlurred] = useState(false);
  const [showAdditionalSMSInfo, setShowAdditionalSMSInfo] = useState(false);
  const [t] = useTranslation();
  const formHeader = useRef(null);

  const showIncomeWarning = incomeBlurred && (values.totalIncome || 0) < MIN_INCOME_WARNING_VALUE;
  const showEmploymentWarning = employmentBlurred && values.incomeSource === Profile.UNEMPLOYED;
  const companySingleName = t('companySingleName');
  const isNY = values.propertyAddress?.state === 'NY';

  const originalPhone = profile?.phoneNumbers[0]?.number || prePopulatedData?.phoneNumber || '';

  useEffect(() => {
    if (!isSubmitting) {
      if (formHeader?.current) {
        const topCtrl = formHeader.current.querySelector('.error');
        topCtrl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [isSubmitting]);

  const { smsVersion, smsVerbiage, smsSecondaryVerbiage, smsTitle } = smsData;

  return (
    <>
      <PageHeader>One click to find your rate - it won’t affect your credit score</PageHeader>
      <Form ref={formHeader}>
        <AddressSectionForm
          id="property-address"
          fieldName="propertyAddress"
          label="Primary address"
          values={values}
          errors={errors}
          touched={touched}
          setFieldValue={setFieldValue}
        />

        <DropdownSelect
          id="financing-purpose"
          label="Financing purpose"
          name="financing-purpose"
          data-test-id="reason"
          value={values.financingPurpose}
          errorText={touched.financingPurpose && errors.financingPurpose}
          required
          onChange={(e) => setFieldValue('financingPurpose', e.target.value)}
        >
          <option value="" disabled />
          {Financing.PURPOSES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </DropdownSelect>

        <CurrencyInput
          id="requested-amount"
          data-test-id="requested-amount"
          label="Requested loan amount (max. loan amount = $50,000)"
          value={values.requestedAmount}
          errorText={touched.requestedAmount && errors.requestedAmount}
          onChange={(v) => setFieldValue('requestedAmount', v)}
          placeholder="$"
          required
        />

        <DropdownSelect
          required
          id="living-arrangement"
          label="Current living arrangement"
          name="living-arrangement"
          data-test-id="living-arrangement"
          value={values.livingArrangement}
          errorText={touched.livingArrangement && errors.livingArrangement}
          onChange={(e) => setFieldValue('livingArrangement', e.target.value)}
        >
          <option value="" disabled />
          {LivingArrangements.TYPES.map(({ value, label }) => (
            <option value={value} key={value}>
              {label}
            </option>
          ))}
        </DropdownSelect>

        <NameSectionForm
          values={values}
          errors={errors}
          touched={touched}
          setFieldValue={setFieldValue}
        />

        <CurrencyInput
          id="total-income"
          data-test-id="totalIncome-amount"
          label="Annual gross income including bonus"
          warning={incomeBlurred && values.totalIncome < MIN_INCOME_WARNING_VALUE}
          value={values.totalIncome}
          errorText={touched.totalIncome && errors.totalIncome}
          onChange={(v) => setFieldValue('totalIncome', v)}
          onBlur={() => {
            setIncomeBlurred(true);
            setFieldTouched('totalIncome', true);
          }}
          placeholder="$"
          maxLength={10}
          required
        />
        <Typography.SmallBodyGrey style={{ marginBottom: '15px' }} id="total-imcome-description">
          Salary or income received by you individually.
        </Typography.SmallBodyGrey>

        {showIncomeWarning && (
          <ConsentCheckbox
            id="income-warning-confirm"
            label="Please confirm that you have entered your yearly gross income."
            checked={incomeWarningCheckboxChecked}
            onChange={() => setIncomeWarningCheckboxChecked(!incomeWarningCheckboxChecked)}
            autoFocus
          />
        )}

        <DropdownSelect
          id="employment-status"
          data-test-id="employment-status"
          label="Source of annual gross income"
          name="incomeSource"
          value={values.incomeSource}
          errorText={touched.incomeSource && errors.incomeSource}
          required
          onChange={(e) => setFieldValue('incomeSource', e.target.value)}
          onBlur={() => setEmploymentBlurred(true)}
        >
          <option value="" disabled />
          {Profile.EMPLOYMENT_TYPE.map((e) => (
            <option key={e.value} value={e.value}>
              {e.label}
            </option>
          ))}
        </DropdownSelect>

        {showEmploymentWarning && (
          <Error>
            We’re unable to lend to unemployed applicants. However, if you rely on income that is
            considered community or marital income in your state, we can use your spouse or
            partner's employment to evaluate your application. If you’d like to use this option,
            please select their employment status above.
          </Error>
        )}

        <PhoneInput
          id="phone"
          label="Phone number"
          data-test-id="phone"
          name="phone"
          value={values.phone}
          errorText={touched.phone && errors.phone}
          onChange={(value) => setFieldValue('phone', value)}
          isNumericString
          required
          descriptiveTextId="phone-number-description"
          format="+1 (###) ###-####"
          disabled={isAuthenticated && originalPhone.length > 0}
        />
        <Typography.SmallBodyGrey style={{ marginBottom: '15px' }} id="phone-number-description">
          Phone number will be used for communications related to your application or account.
        </Typography.SmallBodyGrey>
        {smsVersion === SMS_EXPERIMENT_MAP.variation_1 && (
          <ConsentCheckbox
            id="consent3"
            labelNode={
              <>
                <Typography.SmallBodyGrey>
                  <Typography.SmallBodyBoldGrey as="span">{smsTitle}</Typography.SmallBodyBoldGrey>{' '}
                  {smsVerbiage}
                  {!showAdditionalSMSInfo && (
                    <>
                      {' '}
                      <InlineAnchor
                        onClick={(e) => {
                          e.preventDefault();
                          setShowAdditionalSMSInfo(true);
                        }}
                      >
                        Read more
                      </InlineAnchor>{' '}
                    </>
                  )}
                </Typography.SmallBodyGrey>
                {showAdditionalSMSInfo && (
                  <>
                    <br />
                    <Typography.SmallBodyGrey>{smsSecondaryVerbiage}</Typography.SmallBodyGrey>
                  </>
                )}
              </>
            }
            name="consent3"
            data-test-id="consent3"
            checked={values.consent3 === 'YES'}
            value="YES"
            errorText={touched.consent3 && errors.consent3}
            onChange={(e) => setFieldValue('consent3', e.target.checked ? 'YES' : '')}
            required
            alignTop
          />
        )}

        {!isAuthenticated && (
          <>
            <EmailInput
              id="email"
              label="Email"
              name="email"
              data-test-id="email"
              value={values.email}
              errorText={touched.email && errors.email}
              onChange={(e) => setFieldValue('email', e.target.value)}
              required
            />

            <PasswordSectionForm
              values={values}
              errors={errors}
              touched={touched}
              setFieldValue={setFieldValue}
            />

            <ConsentCheckbox
              id="consent"
              labelNode={
                <>
                  I agree to the{' '}
                  <InlineAnchor href="/terms" target="_blank" rel="noopener noreferrer">
                    Terms of Service
                  </InlineAnchor>{' '}
                  and{' '}
                  <InlineAnchor href="/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </InlineAnchor>
                  .
                </>
              }
              name="consent"
              data-test-id="consent"
              checked={values.consent === 'YES'}
              value="YES"
              errorText={touched.consent && errors.consent}
              onChange={(e) => setFieldValue('consent', e.target.checked ? 'YES' : '')}
              required
            />

            <ConsentCheckbox
              id="consent2"
              labelNode={
                <>
                  I certify that by checking this box I have read {companySingleName}’s{' '}
                  <InlineAnchor
                    href={`${isFigure ? 'https://www.figure.com' : ''}/communications`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Electronic Communications Policy
                  </InlineAnchor>
                  , and consent to receive all legally required notices and disclosures and other
                  communications (“Communications”) from {companySingleName} electronically and not
                  on paper. I also confirm I am able to access, view, retain and print all of the
                  Communications {companySingleName} provides me in electronic form
                </>
              }
              name="consent2"
              data-test-id="consent2"
              checked={values.consent2 === 'YES'}
              value="YES"
              errorText={touched.consent2 && errors.consent2}
              onChange={(e) => setFieldValue('consent2', e.target.checked ? 'YES' : '')}
              alignTop
              required
            />
          </>
        )}

        <Error display="block">{serverError}</Error>

        <AgreeButtonDescription id="agree-button-description">
          By pressing the “I agree” button immediately following this notice, I am providing
          ‘written instructions’ to {companySingleName} under the Fair Credit Reporting Act
          authorizing {companySingleName} to obtain information from my personal credit profile or
          other information from Experian and verify my income with Truework (
          <a href="http://truework.com" target="_blank" rel="noopener noreferrer">
            www.truework.com
          </a>
          ). I authorize {companySingleName} to obtain such information solely to conduct a
          pre-qualification for credit and acknowledge this will not affect my credit score
          {isNY && <sup>1</sup>}.
        </AgreeButtonDescription>

        <AgreeButtonContainer>
          <Button
            primary
            large
            center
            descriptiveTextId="agree-button-description pre-qualification-description"
            type="submit"
            onClick={handleSubmit}
            data-test-id="pq-agree-button"
          >
            I agree
          </Button>

          <DisclosureLegal>Pre-qualification won’t affect your credit score.</DisclosureLegal>
          <Typography.SmallBodyGrey>
            Note: you may not use any portion of this loan for postsecondary education purposes
          </Typography.SmallBodyGrey>
        </AgreeButtonContainer>

        {isNY && (
          <Typography.SmallBodyGrey data-test-id="ny-disclaimer">
            <sup>1</sup> A consumer report may be ordered on you in connection with your application
            for credit. If you ask, we will tell you whether or not one was ordered and if one was,
            the name and address of the consumer reporting agency that provided it. Subsequent
            consumer reports may be requested or used in connection with an update, renewal or
            extension of the credit applied for without further notice to you.
          </Typography.SmallBodyGrey>
        )}
      </Form>
    </>
  );
};

StartApplication.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  serverError: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  profile: PropTypes.shape({
    phoneNumbers: PropTypes.arrayOf(
      PropTypes.shape({
        number: PropTypes.string,
      })
    ),
  }).isRequired,
  prePopulatedData: PropTypes.shape({
    phoneNumber: PropTypes.string,
  }).isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  smsData: PropTypes.string,
};

StartApplication.defaultProps = {
  isAuthenticated: false,
  serverError: '',
  smsData: '',
};

const sanitizePhoneNumber = (phoneNumber = '') => {
  const is12Digits = phoneNumber?.length === 12;
  const hasPlusOne = phoneNumber.substring(0, 2) === '+1';

  if (is12Digits && hasPlusOne) return phoneNumber.substring(2);

  return phoneNumber;
};

export default withFormik({
  enableReinitialize: true,

  mapPropsToValues: ({
    profile = { name: {}, phoneNumbers: [] },
    personalLoan = {},
    prePopulatedData = {},
  }) => ({
    propertyAddress: {
      uuid: profile.addresses?.[0]?.uuid || '',
      street1: profile.addresses?.[0]?.street1 || prePopulatedData.address?.street1 || '',
      street2: profile.addresses?.[0]?.street2 || prePopulatedData.address?.street2 || '',
      city: profile.addresses?.[0]?.city || prePopulatedData.address?.city || '',
      state: profile.addresses?.[0]?.state || prePopulatedData.address?.state || '',
      zip: profile.addresses?.[0]?.zip || prePopulatedData.address?.zip || '',
    },
    livingArrangement: personalLoan.currentLivingArrangement || '',
    name: {
      uuid: profile.name.uuid || '',
      firstName: profile.name.firstName || prePopulatedData.name?.firstName || '',
      lastName: profile.name.lastName || prePopulatedData.name?.lastName || '',
      suffix: profile.name.suffix || '',
    },
    dob:
      dateFormatter.formatDisplayLocalDate(profile.dob) ||
      dateFormatter.formatDisplayLocalDate(prePopulatedData.dob) ||
      '',
    phone: sanitizePhoneNumber(profile.phoneNumbers[0]?.number || prePopulatedData.phoneNumber),
    totalIncome: personalLoan.income?.incomePrimary || prePopulatedData.income || undefined,
    incomeSource: personalLoan?.incomeSources?.[0]?.employmentType || '',
    email: profile.email || prePopulatedData.email || '',
    financingPurpose: personalLoan.reason || '',
    requestedAmount:
      personalLoan.desiredLoanAmount || prePopulatedData.requestedAmount || undefined,
    citizenship: profile.citizenship || '',
    password: '',
    confirmPassword: '',
    consent: '',
    consent2: '',
  }),

  validationSchema: ({ eligibleStates, isAuthenticated = false, isUcc = false }) =>
    yup.lazy(() => {
      let schema = {
        propertyAddress: yup.object().shape({
          street1: yup.string().trim().required('Required'),
          city: yup.string().trim().required('Required'),
          state: yup.string().trim().required('Required'),
          zip: yup
            .string()
            .required('Required')
            .min(5, 'Must be at least 5 digits')
            .max(10, 'Must be less than 10 digits')
            .test(
              'is-a-valid-zip',
              'Must be a valid zip code',
              (value) => !value || /^[0-9]{5}(?:-[0-9]{4})?$/.test(value)
            ),
        }),
        financingPurpose: yup.string().required('Required'),
        requestedAmount: yup
          .number()
          .required('Required')
          .min(1, 'Amount must be greater than $0')
          .max(50000, 'Amount must be less than or equal to $50,000'),
        livingArrangement: yup.string().required('Required'),
        name: yup
          .object()
          .shape({
            firstName: yup.string().trim().required('Required'),
            lastName: yup.string().trim().required('Required'),
            suffix: yup.string(),
          })
          .required('Required'),
        dob: yup
          .string()
          .required('Required')
          .test('is-a-valid-date', 'DOB must be MM/DD/YYYY', (value) => dateUtil.dateIsValid(value))
          .test('is-not-age-restricted', 'Must be $1 years or older', function (value) {
            const state = this.parent.propertyAddress.state;
            const dob = value;
            const isInAgeRestrictedState = !!AgeRestriction.AGE_BY_STATE[state] ?? false;
            const stateRestrictedAge = AgeRestriction.AGE_BY_STATE?.[state];
            const applicantsAge = differenceInYears(new Date(), dob);

            if (isInAgeRestrictedState && applicantsAge < stateRestrictedAge) {
              return this.createError({
                message: `Must be ${stateRestrictedAge} years or older`,
              });
            } else {
              return true;
            }
          })
          .test('is-at-last-eighteen', 'Must be 18 years or older', (value) =>
            dateUtil.isEighteen(value)
          )
          .test('is-at-least-1900', 'Must be at least 1900', (value) =>
            dateUtil.isAtLeast1900(value)
          ),
        phone: yup
          .string()
          .required('Required')
          .test(
            'is-phone-number',
            'Must be a valid phone number',
            (value) => !value || value.toString().length === 10
          )
          .test(
            'is_pos_one_not_one',
            'Do not enter “1” as first digit of area code',
            (value) => value?.toString()[0] !== '1'
          ),
        totalIncome: yup
          .number()
          .required('Required')
          .min(1, 'Must be at least $1')
          .max(9999999, 'Must be less than $10,000,000'),
        incomeOther: yup.object().shape({
          amount: yup
            .number()
            .min(1, 'Must be at least $1')
            .max(9999999, 'Must be less than $10,000,000'),
        }),
        incomeSource: yup
          .string()
          .required('Required')
          .test('is-unemployed', 'Required', (value) => value !== Profile.UNEMPLOYED),
        citizenship: yup
          .string()
          .required('Required')
          .test(
            'is-eligible',
            'Borrower must be a U.S. citizen or Permanent Resident with 10-year non-conditional Permanent Resident card',
            (value) => value === 'CITIZEN' || value === 'PERMANENT_RESIDENT'
          ),
      };
      if (!isAuthenticated)
        schema = {
          ...schema,
          ...{
            email: yup.string().required('Required').email('Invalid'),
            password: yup
              .string()
              .required('Required')
              .min(9, 'Must be at least 9 characters')
              .max(512, 'Must be at most 512 characters')
              .test(
                'is-strong',
                'Must contain at least one letter, number, and a special character',
                (value) =>
                  !value ||
                  value.length < 9 ||
                  /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z\d]).{9,}$/.test(value)
              ),
            confirmPassword: yup
              .string()
              .required('Required')
              .min(9, 'Must be at least 9 characters')
              .max(512, 'Must be at most 512 characters')
              .test('is-a-match', 'Must match', function match(value) {
                return value == null || this.parent.password === value;
              }),
            consent: yup.string().required('Required'),
            consent2: yup.string().required('Required'),
            consent3: yup.string(),
          },
        };
      return yup.object().shape(schema);
    }),
  handleSubmit: (values, { props }) => props.handleSubmit(values),
})(StartApplication);
